import React, { memo, useState, useCallback, useEffect, useMemo } from 'react';

import { areEqual } from 'Utils/equalityChecks';
import { Api } from 'Utils/api';
import { useGHL } from 'Context/GHL';

import { NewLeadForm } from './NewLeadForm';
import { ExistingLeadForm } from './ExistingLeadForm';
import { LeadsWithReferrals } from './LeadsWithReferrals';
import { LeadsWithoutReferrals } from './LeadsWithoutReferrals';
import { ReferralFees } from './ReferralFees';
import { FormFieldSettings } from './FormFieldSettings';
import { Option, PipelineOption, CustomFieldConfig, FormFieldSetting, GhlCustomFieldResponse } from './types';

import classes from './leadForms.module.css';

const PIPELINE_STORAGE_KEY_PREFIX = 'ghl_pipeline_selection:';

function LeadFormsContainer() {
  const { isAuthenticated, locationId, ssoToken, isAdmin, error: ghlError, isLoading: ghlLoading } = useGHL();

  // Loading states
  const [isLoadingContacts, setIsLoadingContacts] = useState(false);
  const [isLoadingCompanies, setIsLoadingCompanies] = useState(false);
  const [isLoadingPipelines, setIsLoadingPipelines] = useState(false);
  const [isLoadingCustomFields, setIsLoadingCustomFields] = useState(false);

  // Data states
  const [referralPartnerOptions, setReferralPartnerOptions] = useState<Option[]>([]);
  const [existingLeadOptions, setExistingLeadOptions] = useState<Option[]>([]);
  const [companyOptions, setCompanyOptions] = useState<Option[]>([]);
  const [pipelineOptions, setPipelineOptions] = useState<PipelineOption[]>([]);
  const [customFieldConfig, setCustomFieldConfig] = useState<CustomFieldConfig>({
    typeOfLossId: '',
    typeOfLossOptions: [],
    classId: '',
    classOptions: [],
    industryId: '',
    industryOptions: [],
    notesId: '',
    contactReferredById: '',
    contactReferredBy: '',
    oppReferredById: '',
    oppReferredBy: '',
    leadValueId: '',
    leadValueOptions: [],
    officeId: '',
    officeOptions: [],
  });

  // Tab state - 'form' is active by default
  const [activeTab, setActiveTab] = useState<'form' | 'with-referrals' | 'without-referrals' | 'referral-fees' | 'settings'>('form');

  // Form field config state
  const [formFieldSettings, setFormFieldSettings] = useState<FormFieldSetting[]>([]);
  const [rawCustomFields, setRawCustomFields] = useState<GhlCustomFieldResponse[]>([]);

  // Accordion state - 'new' is expanded by default
  const [expandedSection, setExpandedSection] = useState<'new' | 'existing' | null>('new');

  const pipelineStorageKey = useMemo(
    () => (locationId ? `${PIPELINE_STORAGE_KEY_PREFIX}${locationId}` : null),
    [locationId]
  );

  // Load referral partners (contacts with type referral_partner)
  const loadReferralPartners = useCallback(async () => {
    if (!locationId || !ssoToken) return;

    setIsLoadingContacts(true);
    try {
      // Backend endpoint is /ghl/embed/referral-partners
      const response = await Api.get('/ghl/embed/referral-partners', {
        params: { limit: 500 },
        headers: { 'X-GHL-SSO': ssoToken },
      });

      // Handle both GHL format { contacts: [...] } and Laravel format { data: [...] }
      const contacts = Array.isArray(response?.data?.contacts)
        ? response.data.contacts
        : Array.isArray(response?.data?.data)
          ? response.data.data
          : Array.isArray(response?.data)
            ? response.data
            : [];

      const options: Option[] = contacts.map((contact: any) => ({
        id: contact.id,
        // Support both snake_case (Laravel API) and camelCase (GHL API) field names
        label: contact.display_name
          || contact.displayName
          || `${contact.first_name || contact.firstName || ''} ${contact.last_name || contact.lastName || ''}`.trim()
          || 'Unknown',
        companyName: contact.business?.name || contact.company_name || contact.companyName || '',
      }));

      setReferralPartnerOptions(options);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Failed to load referral partners', error);
    } finally {
      setIsLoadingContacts(false);
    }
  }, [locationId, ssoToken]);

  // Load existing leads (contacts with type lead)
  const loadExistingLeads = useCallback(async () => {
    if (!locationId || !ssoToken) return;

    try {
      const response = await Api.get('/ghl/embed/contacts', {
        params: {
          type: 'lead',
          limit: 500,
        },
        headers: { 'X-GHL-SSO': ssoToken },
      });

      // Handle both GHL format { contacts: [...] } and Laravel format { data: [...] }
      const contacts = Array.isArray(response?.data?.contacts)
        ? response.data.contacts
        : Array.isArray(response?.data?.data)
          ? response.data.data
          : Array.isArray(response?.data)
            ? response.data
            : [];

      const options: Option[] = contacts.map((contact: any) => ({
        id: contact.id,
        // Support both snake_case (Laravel API) and camelCase (GHL API) field names
        label: contact.display_name
          || contact.displayName
          || `${contact.first_name || contact.firstName || ''} ${contact.last_name || contact.lastName || ''}`.trim()
          || 'Unknown',
      }));

      setExistingLeadOptions(options);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Failed to load existing leads', error);
    }
  }, [locationId, ssoToken]);

  // Load companies/businesses
  const loadCompanies = useCallback(async () => {
    if (!locationId || !ssoToken) return;

    setIsLoadingCompanies(true);
    try {
      const response = await Api.get('/ghl/embed/businesses', {
        params: { locationId },
        headers: { 'X-GHL-SSO': ssoToken },
      });

      // Handle both GHL format { businesses: [...] } and Laravel format { data: [...] }
      const businesses = Array.isArray(response?.data?.businesses)
        ? response.data.businesses
        : Array.isArray(response?.data?.data)
          ? response.data.data
          : Array.isArray(response?.data)
            ? response.data
            : [];

      const options: Option[] = businesses.map((business: any) => ({
        id: business.id,
        label: business.name || 'Unknown',
      }));

      setCompanyOptions(options);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Failed to load companies', error);
    } finally {
      setIsLoadingCompanies(false);
    }
  }, [locationId, ssoToken]);

  // Load pipelines
  const loadPipelines = useCallback(async () => {
    if (!locationId || !ssoToken) return;

    setIsLoadingPipelines(true);
    try {
      const response = await Api.get('/ghl/embed/pipelines', {
        params: { locationId },
        headers: { 'X-GHL-SSO': ssoToken },
      });

      // GHL API returns { pipelines: [...] }
      const pipelines = Array.isArray(response?.data?.pipelines)
        ? response.data.pipelines
        : Array.isArray(response?.data?.data)
          ? response.data.data
          : Array.isArray(response?.data)
            ? response.data
            : [];

      const options: PipelineOption[] = pipelines.map((pipeline: any) => ({
        id: pipeline.id,
        label: pipeline.name || 'Unknown',
        stages: (pipeline.stages || [])
          .sort((a: any, b: any) => (a.sort_order ?? 0) - (b.sort_order ?? 0))
          .map((stage: any) => ({
            id: stage.id,
            label: stage.name || 'Unknown',
          })),
      }));

      setPipelineOptions(options);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Failed to load pipelines', error);
    } finally {
      setIsLoadingPipelines(false);
    }
  }, [locationId, ssoToken]);

  // Load custom fields
  const loadCustomFields = useCallback(async () => {
    if (!locationId || !ssoToken) return;

    setIsLoadingCustomFields(true);
    try {
      const response = await Api.get('/ghl/embed/custom-fields', {
        params: { locationId },
        headers: { 'X-GHL-SSO': ssoToken },
      });

      // Handle both GHL format { customFields: [...] } and Laravel format { data: [...] }
      const customFields = Array.isArray(response?.data?.customFields)
        ? response.data.customFields
        : Array.isArray(response?.data?.data)
          ? response.data.data
          : Array.isArray(response?.data)
            ? response.data
            : [];

      const config: CustomFieldConfig = {
        typeOfLossId: '',
        typeOfLossOptions: [],
        classId: '',
        classOptions: [],
        industryId: '',
        industryOptions: [],
        notesId: '',
        contactReferredById: '',
        contactReferredBy: '',
        oppReferredById: '',
        oppReferredBy: '',
        leadValueId: '',
        leadValueOptions: [],
        officeId: '',
        officeOptions: [],
      };

      customFields.forEach((field: any) => {
        const fieldKey = field.fieldKey || field.field_key;
        const picklistOptions = field.picklistOptions || field.picklist_options || [];

        switch (fieldKey) {
          case 'contact.type_of_loss':
            config.typeOfLossId = field.id;
            config.typeOfLossOptions = ['', ...picklistOptions];
            break;
          case 'contact.class':
            config.classId = field.id;
            config.classOptions = ['', ...picklistOptions];
            break;
          case 'contact.industry':
            config.industryId = field.id;
            config.industryOptions = ['', ...picklistOptions];
            break;
          case 'contact.notes':
            config.notesId = field.id;
            break;
          case 'contact.referred_by':
            config.contactReferredBy = field.id;
            break;
          case 'contact.referred_by_id':
            config.contactReferredById = field.id;
            break;
          case 'opportunity.referred_by':
            config.oppReferredBy = field.id;
            break;
          case 'opportunity.referred_by_id':
            config.oppReferredById = field.id;
            break;
          default:
            break;
        }
      });

      // Store raw custom fields for the settings panel
      setRawCustomFields(customFields);

      setCustomFieldConfig(config);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Failed to load custom fields', error);
    } finally {
      setIsLoadingCustomFields(false);
    }
  }, [locationId, ssoToken]);

  // Load form field config
  const loadFormFieldConfig = useCallback(async () => {
    if (!locationId || !ssoToken) return;

    try {
      const response = await Api.get('/ghl/embed/form-field-config', {
        headers: { 'X-GHL-SSO': ssoToken },
      });

      const fields = response?.data?.data || [];
      setFormFieldSettings(fields);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Failed to load form field config', error);
    }
  }, [locationId, ssoToken]);

  // Load all data when authenticated
  useEffect(() => {
    if (isAuthenticated && locationId && ssoToken) {
      loadReferralPartners();
      loadExistingLeads();
      loadCompanies();
      loadPipelines();
      loadCustomFields();
      loadFormFieldConfig();
    }
  }, [isAuthenticated, locationId, ssoToken, loadReferralPartners, loadExistingLeads, loadCompanies, loadPipelines, loadCustomFields, loadFormFieldConfig]);

  // Accordion toggle handler
  const handleAccordionToggle = useCallback((section: 'new' | 'existing') => {
    setExpandedSection((prev) => (prev === section ? null : section));
  }, []);

  // Add new referral partner to options
  const handleReferralPartnerCreated = useCallback((newPartner: Option) => {
    setReferralPartnerOptions((prev) => [newPartner, ...prev]);
  }, []);

  // Add new company to options
  const handleCompanyCreated = useCallback((newCompany: Option) => {
    setCompanyOptions((prev) => [newCompany, ...prev]);
  }, []);

  // Add new lead to existing leads options
  const handleLeadCreated = useCallback((newLead: Option) => {
    setExistingLeadOptions((prev) => [newLead, ...prev]);
  }, []);

  // Handle GHL loading/error states
  if (ghlLoading) {
    return (
      <div className={classes.container}>
        <div className={classes.loadingState}>
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p>Connecting to RocketCRM...</p>
        </div>
      </div>
    );
  }

  if (ghlError) {
    return (
      <div className={classes.container}>
        <div className={`alert alert-danger ${classes.errorCard}`}>
          <h4>Connection Error</h4>
          <p>{ghlError}</p>
          <p className={classes.helpText}>Please ensure this page is accessed from within RocketCRM.</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <div className={classes.container}>
        <div className={`alert alert-warning ${classes.warningCard}`}>
          <h4>Not Authenticated</h4>
          <p>Please access this page from within RocketCRM to use lead forms.</p>
        </div>
      </div>
    );
  }

  if (!locationId) {
    return (
      <div className={classes.container}>
        <div className={`alert alert-warning ${classes.warningCard}`}>
          <h4>No Location Selected</h4>
          <p>Select a location in RocketCRM to use lead forms.</p>
        </div>
      </div>
    );
  }

  const hasPipelines = pipelineOptions.length > 0;

  return (
    <div className={classes.container}>
      {/* Tab Navigation */}
      <ul className="nav nav-tabs mb-3">
        <li className="nav-item">
          <button
            type="button"
            className={`nav-link ${activeTab === 'form' ? 'active' : ''}`}
            onClick={() => setActiveTab('form')}
          >
            FORM
          </button>
        </li>
        <li className="nav-item">
          <button
            type="button"
            className={`nav-link ${activeTab === 'with-referrals' ? 'active' : ''}`}
            onClick={() => setActiveTab('with-referrals')}
          >
            LEADS WITH REFERRALS
          </button>
        </li>
        <li className="nav-item">
          <button
            type="button"
            className={`nav-link ${activeTab === 'without-referrals' ? 'active' : ''}`}
            onClick={() => setActiveTab('without-referrals')}
          >
            LEADS WITHOUT REFERRALS
          </button>
        </li>
        {isAdmin && (
          <li className="nav-item">
            <button
              type="button"
              className={`nav-link ${activeTab === 'referral-fees' ? 'active' : ''}`}
              onClick={() => setActiveTab('referral-fees')}
            >
              REFERRAL FEES
            </button>
          </li>
        )}
        <li className="nav-item">
          <button
            type="button"
            className={`nav-link ${activeTab === 'settings' ? 'active' : ''}`}
            onClick={() => setActiveTab('settings')}
          >
            SETTINGS
          </button>
        </li>
      </ul>

      {/* Form Tab Content */}
      {activeTab === 'form' && (
        <>
          {!hasPipelines && !isLoadingPipelines && (
            <div className="alert alert-warning mb-3">
              No pipelines are available for this location. Add a pipeline in RocketCRM to enable referrals.
            </div>
          )}

          {/* Accordion */}
          <div className="accordion" id="leadFormsAccordion">
            {/* Existing Lead Section */}
            <div className="accordion-item">
              <h2 className="accordion-header">
                <button
                  className={`accordion-button ${expandedSection !== 'existing' ? 'collapsed' : ''}`}
                  type="button"
                  onClick={() => handleAccordionToggle('existing')}
                  aria-expanded={expandedSection === 'existing'}
                >
                  EXISTING LEAD
                </button>
              </h2>
              <div className={`accordion-collapse collapse ${expandedSection === 'existing' ? 'show' : ''}`}>
                <div className="accordion-body">
                  <ExistingLeadForm
                    locationId={locationId}
                    ssoToken={ssoToken}
                    existingLeadOptions={existingLeadOptions}
                    referralPartnerOptions={referralPartnerOptions}
                    pipelineOptions={pipelineOptions}
                    companyOptions={companyOptions}
                    customFieldConfig={customFieldConfig}
                    formFieldSettings={formFieldSettings}
                    isLoadingContacts={isLoadingContacts}
                    isLoadingCompanies={isLoadingCompanies}
                    isLoadingPipelines={isLoadingPipelines}
                    pipelineStorageKey={pipelineStorageKey}
                    onReferralPartnerCreated={handleReferralPartnerCreated}
                    onCompanyCreated={handleCompanyCreated}
                  />
                </div>
              </div>
            </div>

            {/* Add New Lead Section */}
            <div className="accordion-item">
              <h2 className="accordion-header">
                <button
                  className={`accordion-button ${expandedSection !== 'new' ? 'collapsed' : ''}`}
                  type="button"
                  onClick={() => handleAccordionToggle('new')}
                  aria-expanded={expandedSection === 'new'}
                >
                  ADD NEW LEAD
                </button>
              </h2>
              <div className={`accordion-collapse collapse ${expandedSection === 'new' ? 'show' : ''}`}>
                <div className="accordion-body">
<NewLeadForm
                    locationId={locationId}
                    ssoToken={ssoToken}
                    referralPartnerOptions={referralPartnerOptions}
                    pipelineOptions={pipelineOptions}
                    companyOptions={companyOptions}
                    customFieldConfig={customFieldConfig}
                    formFieldSettings={formFieldSettings}
                    isLoadingContacts={isLoadingContacts}
                    isLoadingCompanies={isLoadingCompanies}
                    isLoadingPipelines={isLoadingPipelines}
                    pipelineStorageKey={pipelineStorageKey}
                    onReferralPartnerCreated={handleReferralPartnerCreated}
                    onCompanyCreated={handleCompanyCreated}
                    onLeadCreated={handleLeadCreated}
                  />
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Leads With Referrals Tab Content */}
      {activeTab === 'with-referrals' && (
        <LeadsWithReferrals
          locationId={locationId}
          ssoToken={ssoToken}
          referralPartnerOptions={referralPartnerOptions}
          customFieldConfig={customFieldConfig}
          isLoadingContacts={isLoadingContacts}
        />
      )}

      {/* Leads Without Referrals Tab Content */}
      {activeTab === 'without-referrals' && (
        <LeadsWithoutReferrals
          locationId={locationId}
          ssoToken={ssoToken}
          referralPartnerOptions={referralPartnerOptions}
          customFieldConfig={customFieldConfig}
          isLoadingContacts={isLoadingContacts}
        />
      )}

      {/* Referral Fees Tab Content (Admin Only) */}
      {activeTab === 'referral-fees' && isAdmin && (
        <ReferralFees
          locationId={locationId}
          ssoToken={ssoToken}
        />
      )}

      {activeTab === 'settings' && (
        <FormFieldSettings
          locationId={locationId}
          ssoToken={ssoToken}
          customFields={rawCustomFields}
          savedSettings={formFieldSettings}
          onSave={setFormFieldSettings}
        />
      )}
    </div>
  );
}

const LeadFormsContainerMemo = memo(LeadFormsContainer, areEqual);

export { LeadFormsContainerMemo as LeadFormsContainer };
